/** @jsx jsx */
import React from 'react';
import { useMonth, UseMonthProps } from './useDatePicker';
import { Tiles, jsx } from '@front10/base-elements';
import { useIsMobileView } from '@front10/utils';
import { formatDate as fallBackFormat } from '../utils';
import { useDatePickerI18n } from './datePickerI18n';
import { Day } from './Day';
import { BlackoutDates } from './../../containers/VerticalBookingContainer/blackoutDates';

interface DayFormat {
  dayFormat?: string;
}
interface BlackoutDates_ {
  blackoutDatesIntegration?: boolean;
  fetchBlackoutDates?: BlackoutDates | null;
  focusedInput?: string | null;
}

export type MonthProps = DayFormat &
  BlackoutDates_ &
  Pick<UseMonthProps, 'year' | 'month' | 'firstDayOfWeek'>;

export const Month = React.forwardRef<HTMLDivElement, MonthProps>(
  ({ year, month, firstDayOfWeek, dayFormat, ...rest }, ref) => {
    const { months, weekDays: weeks, format = fallBackFormat } = useDatePickerI18n();
    const { days, weekdayLabels, monthLabel } = useMonth({
      year,
      month,
      firstDayOfWeek,
      monthLabelFormat: months
        ? (date) => `${months[date.getMonth()]} ${date.getFullYear()}`
        : undefined,
      weekdayLabelFormat: weeks ? (value) => weeks[value.getDay()] : undefined,
      dayLabelFormat: (date: Date) => {
        return '' + format(date, dayFormat ?? 'd');
      }
    });
    const isMobile = useIsMobileView();
    const monthLabelAlign = isMobile ? 'left' : 'center';
    return (
      <div
        ref={ref}
        sx={{
          variant: !isMobile
            ? 'layout.datepickerStyles.month'
            : 'layout.datepickerStyles.monthMobile'
        }}
      >
        <div
          sx={{ color: 'text', variant: 'text.fontSize4' }}
          css={{
            textAlign: monthLabelAlign,
            margin: '0 0 16px',
            fontWeight: 'normal'
          }}
        >
          <span>{monthLabel}</span>
        </div>
        <Tiles
          columns={7}
          sx={{ variant: 'layout.datepickerStyles.weeks' }}
          css={{
            justifyContent: 'center',
            marginBottom: '10px',
            fontSize: '10px'
          }}
        >
          {weekdayLabels.map((dayLabel) => (
            <div
              sx={{ color: 'text', variant: 'text.fontSize5' }}
              css={{ textAlign: 'center', fontWeight: 'bold' }}
              key={dayLabel}
            >
              {dayLabel}
            </div>
          ))}
        </Tiles>
        <Tiles
          columns={7}
          selectChildrenWithoutType
          sx={{ justifyContent: 'center', variant: 'layout.datepickerStyles.days' }}
        >
          {days.map((day, index) => {
            if (typeof day === 'object') {
              return (
                <Day
                  date={day.date}
                  key={day.date.toString()}
                  dayLabel={day.dayLabel}
                  blackoutDatesIntegration={rest?.blackoutDatesIntegration}
                  fetchBlackoutDates={rest?.fetchBlackoutDates}
                  focusedInput={rest?.focusedInput}
                />
              );
            }
            return <div key={index} />;
          })}
        </Tiles>
      </div>
    );
  }
);
